import type { ApolloError } from '@apollo/client'
import { Loader } from '@mantine/core'

import Empty from 'src/components/Feedback/Empty/Empty'
import { cn } from 'src/utils'

import type { CarrierSearchResult } from '../carrier.types'

import CarrierResultRow from './CarrierResultRow'

export interface CarrierSearchResultsProps {
  /** The term the results belong to, echoed back in the empty state. */
  term: string
  results: CarrierSearchResult[]
  totalCount: number
  loading: boolean
  error?: ApolloError
  /** True while the term is too short to search on. */
  awaitingInput: boolean
  minimumCharacters: number
  onSelect: (carrierId: string) => void
}

const CarrierSearchResults = ({
  term,
  results,
  totalCount,
  loading,
  error,
  awaitingInput,
  minimumCharacters,
  onSelect,
}: CarrierSearchResultsProps) => {
  if (awaitingInput) {
    return (
      <Empty
        title="Search the carrier directory"
        subtitle={`Type at least ${minimumCharacters} characters to search by DOT number or name.`}
      />
    )
  }

  // A failed request must never render as "no carriers found" — an admin who
  // believes that will go and create a duplicate carrier.
  if (error) {
    return (
      <div className="rounded-lg border border-doubleNickel-error-200 bg-doubleNickel-error-25 p-4 text-sm text-doubleNickel-error-700">
        The directory could not be searched, so this is not an answer about{' '}
        <span className="font-medium">{term}</span>. {error.message}
      </div>
    )
  }

  if (loading && results.length === 0) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-doubleNickel-gray-500">
        <Loader size="sm" />
        Searching
      </div>
    )
  }

  if (results.length === 0) {
    return (
      <Empty
        title="No carriers found"
        subtitle={`Nothing in the directory matches "${term}".`}
      />
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        aria-live="polite"
        className="flex items-center gap-2 text-xs text-doubleNickel-gray-500"
      >
        {loading ? (
          <>
            <Loader size="xs" />
            Searching
          </>
        ) : (
          <>
            {totalCount} match{totalCount === 1 ? '' : 'es'}
            {totalCount > results.length &&
              ` — showing the first ${results.length}`}
          </>
        )}
      </div>
      <ul
        className={cn(
          'overflow-hidden rounded-lg border border-doubleNickel-gray-200 transition-opacity',
          // Dimmed while a newer term is in flight, so a stale list is never
          // presented as the answer to what is currently typed.
          loading && 'opacity-50'
        )}
      >
        {results.map((carrier, index) => (
          <li
            key={carrier.carrierId}
            className={
              index > 0 ? 'border-t border-doubleNickel-gray-200' : undefined
            }
          >
            <CarrierResultRow carrier={carrier} onSelect={onSelect} />
          </li>
        ))}
      </ul>
    </div>
  )
}

export default CarrierSearchResults
